import {
  NavigateBeforeRounded,
  NavigateNextRounded,
} from "@mui/icons-material";
import {
  capitalize,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Box } from "@mui/system";
import { LegendPerItemProps } from "@mui/x-charts/ChartsLegend/LegendPerItem";
import type { PimoReactComponent } from "@pimo/pimo-app-builder";
import {
  MuiLineChartCard,
  type MuiLineChartCardProps,
} from "@pimo/pimo-components";
import { getMonthAndYear, NUMBER_TO_MONTH } from "aint-utils";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  convertToYearlyData,
  generateMonthLabels,
} from "../../app/helpers/chart-data-helpers";

export type LineChartSerie = {
  id: string;
  label: string;
  data: (number | null)[];
  color?: string;
  connectNulls?: boolean;
  curve?: "linear" | "natural";
  showMark?: (params: { index: number }) => boolean;
};

const AMOUNT_OF_MONTHS_SHOWN = 13;
const AMOUNT_OF_YEARS_SHOWN = 8;
const SCALES = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
] as const;
export const LineChartCard: PimoReactComponent<
  MuiLineChartCardProps & {
    initialMonth: number;
    initialYear: string;
    cardTitle?: string;
    showSeriesFilter?: boolean;
    currency?: string;
  }
> = (props) => {
  const [allSeries, setAllSeries] = useState<LineChartSerie[]>([]);
  // array holding how the displayed series should be filtered.
  const [filterSeriesBy, setFilterSeriesBy] = useState<string[]>(
    props.series.map(({ label }) => label)
  );
  // all Series filtered by "filterSeriesBy"
  const [filteredSeries, setFilteredSeries] = useState<LineChartSerie[]>([]);

  const [viewState, setViewState] = useState({
    year: "",
    month: 0,
    scale: "monthly" as "monthly" | "yearly",
  });
  const [forecastLimit, setForecastLimit] = useState<number | undefined>(
    undefined
  );
  const [isNextIncrementPossible, setIsNextIncrementPossible] = useState(false);
  const [isPreviousIncrementPossible, setIsPreviousIncrementPossible] =
    useState(false);
  const [maxValue, setMaxValue] = useState(0);
  const [xAxisProps, setXAxisProps] = useState([
    {
      tickSize: 1,
      tickMaxStep: 1,
      tickMinStep: 1,
      label: "",
      data: Array.from(
        {
          length: AMOUNT_OF_MONTHS_SHOWN,
        },
        (_, index) => index
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      valueFormatter: (_: number) => {
        return "" as string;
      },
    },
  ]);
  const memoizedSeries = useMemo(() => props.series, [props.series]);
  const seriesMaxLength = Math.max(
    ...memoizedSeries.map((seriesData) => seriesData?.data?.length || 0)
  );
  const currentPeriodLabel = useRef("");

  const [currentMonth, currentYear] = getMonthAndYear();

  useEffect(() => {
    const [month, year] = getMonthAndYear(new Date(), -6);

    setViewState((prevState) => ({
      ...prevState,
      month,
      year,
    }));
  }, []);

  useEffect(() => {
    let labelsToDisplay = generateMonthLabels(
      viewState.month,
      viewState.year,
      AMOUNT_OF_MONTHS_SHOWN
    );

    if (viewState.scale === "yearly") {
      labelsToDisplay = Array.from(
        { length: AMOUNT_OF_YEARS_SHOWN },
        (_, index) => (parseInt(props.initialYear) + index).toString()
      );
    }

    setXAxisProps([
      {
        tickSize: 1,
        tickMaxStep: 1,
        tickMinStep: 1,
        label: "",
        data: Array.from(
          {
            length:
              viewState.scale === "monthly"
                ? AMOUNT_OF_MONTHS_SHOWN
                : AMOUNT_OF_YEARS_SHOWN,
          },
          (_, index) => index
        ),
        valueFormatter: (val: number) => {
          return labelsToDisplay[val];
        },
      },
    ]);

    currentPeriodLabel.current = `${labelsToDisplay[0]} - ${
      labelsToDisplay[AMOUNT_OF_MONTHS_SHOWN - 1]
    }`;
  }, [props.initialYear, viewState.month, viewState.year, viewState.scale]);

  // Update the data displayed in the line chart (seriesToDisplay) and calculate whether navigating to the previous or next year is possible
  useEffect(() => {
    if (viewState.year == null || viewState.month == null) return;

    const currentViewStartMonthYear =
      capitalize(NUMBER_TO_MONTH[viewState.month]) + " " + viewState.year;

    const seriesMonths = generateMonthLabels(
      props.initialMonth,
      props.initialYear,
      seriesMaxLength
    );

    // Determine the index of the currently displayed start month in the overall data series
    const monthlyStartIndex = seriesMonths.indexOf(currentViewStartMonthYear);

    const currentMonthAsIndexInSeries = seriesMonths.indexOf(
      `${capitalize(NUMBER_TO_MONTH[currentMonth])} ${currentYear}`
    );

    if (viewState.scale === "yearly") {
      setForecastLimit(+currentYear - +props.initialYear);
    } else {
      const isSeriesBeforeCurrentDate =
        viewState.year < currentYear ||
        (viewState.year === currentYear && viewState.month < currentMonth);

      setForecastLimit(
        currentMonthAsIndexInSeries === -1
          ? isSeriesBeforeCurrentDate
            ? seriesMonths.length - 1
            : 0
          : currentMonthAsIndexInSeries - monthlyStartIndex - 1 < 0
            ? 0
            : currentMonthAsIndexInSeries - monthlyStartIndex - 1
      );
    }

    // Update the currently shown data series based on the viewState.scale and startMonthIndex
    const updatedSeries = memoizedSeries.map((seriesData) => {
      let limitedData;
      if (viewState.scale === "yearly") {
        limitedData = convertToYearlyData(seriesData.data, props.initialMonth);
      } else {
        limitedData =
          monthlyStartIndex !== -1
            ? seriesData.data.slice(
                monthlyStartIndex,
                monthlyStartIndex + AMOUNT_OF_MONTHS_SHOWN
              )
            : [];
      }

      return { ...seriesData, data: limitedData };
    });

    setAllSeries(updatedSeries);
    setFilteredSeries(
      [...updatedSeries].filter((series) =>
        filterSeriesBy.includes(series.label)
      )
    );

    // Determine if navigation to the previous and the next year is possible
    setIsPreviousIncrementPossible(
      viewState.scale === "yearly"
        ? monthlyStartIndex > 11
        : monthlyStartIndex > 0
    );
    setIsNextIncrementPossible(
      viewState.scale === "yearly"
        ? monthlyStartIndex + 12 < seriesMonths.length
        : monthlyStartIndex + 13 < seriesMonths.length
    );
  }, [
    viewState,
    memoizedSeries,
    props.initialMonth,
    props.initialYear,
    seriesMaxLength,
    currentMonth,
    currentYear,
    filterSeriesBy,
  ]);

  // Calculate and update the maximum value across all series data points in seriesToDisplay
  useEffect(() => {
    let maxValue = 1;

    allSeries.forEach((seriesData) => {
      seriesData.data?.forEach((value) => {
        maxValue = Math.max(maxValue, value || 0);
      });
    });
    setMaxValue(maxValue);
  }, [allSeries]);

  const handleScaleChange = (event: SelectChangeEvent<unknown>) => {
    const newScale = event.target.value as "yearly" | "monthly";
    setViewState((prevState) => ({ ...prevState, scale: newScale }));
  };

  const handlePrevIncrement = () => {
    if (!isPreviousIncrementPossible) return;

    if (viewState.scale === "yearly") {
      setViewState((prevState) => ({
        ...prevState,
        year: (parseInt(prevState.year, 10) - 1).toString(),
      }));
    } else {
      setViewState((prevState) => {
        let month = prevState.month - 1;
        let year = parseInt(prevState.year, 10);
        if (month < 0) {
          month = 11;
          year--;
        }

        return {
          ...prevState,
          month,
          year: year.toString(),
        };
      });
    }
  };

  const handleNextIncrement = () => {
    if (!isNextIncrementPossible) return;

    if (viewState.scale === "yearly") {
      setViewState((prevState) => ({
        ...prevState,
        year: (parseInt(prevState.year, 10) + 1).toString(),
      }));
    } else {
      setViewState((prevState) => {
        let month = prevState.month + 1;
        let year = parseInt(prevState.year, 10);
        if (month > 11) {
          month = 0;
          year++;
        }

        return {
          ...prevState,
          month,
          year: year.toString(),
        };
      });
    }
  };

  const yAxisProps = [
    {
      max: Math.ceil(maxValue + maxValue * 0.1),
      tickSize: 1,
      label: `Impact (Mn ${props.currency})`,
    },
  ];

  const legendItems: LegendPerItemProps["itemsToDisplay"] = [
    { color: "none", label: "planned     ", id: "plannedLabel" },
    { color: "none", label: "actual     ", id: "actualLabel" },
  ];

  return (
    <MuiLineChartCard
      title={props.cardTitle ?? ""}
      series={filteredSeries}
      marginTop={0}
      cardProps={{
        ...props.cardProps,

        leftSlot: (
          <Box
            component="img"
            src="maturity.svg"
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
            }}
          />
        ),
        rightSlot: props.showSeriesFilter === true && (
          <FormControl>
            <InputLabel sx={{ zIndex: 0 }}>Series:</InputLabel>
            <Select
              multiple
              value={filterSeriesBy}
              input={<OutlinedInput label="Series" />}
              renderValue={(selected) => {
                if (selected.length === props.series.length) {
                  return "All";
                }

                return selected.join(", ");
              }}
              sx={{
                width: "100%",
                height: "36px",
              }}
              onChange={(event) => {
                const {
                  target: { value },
                } = event;

                if (typeof value === "string") {
                  return;
                }

                setFilterSeriesBy(value);
              }}
            >
              {allSeries.map((series) => (
                <MenuItem key={series.id} value={series.label}>
                  <Checkbox
                    checked={filterSeriesBy.indexOf(series.label) > -1}
                  />
                  <ListItemText primary={series.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      }}
      xAxis={xAxisProps}
      yAxis={yAxisProps}
      forecastLimit={forecastLimit}
      legendItems={legendItems}
      sx={{
        ".MuiChartsLegend-root": {
          ".MuiChartsLegend-series:nth-of-type(-n+2)": {
            ".MuiChartsLegend-mark": {
              y: "0 !important",
              height: "0.1px",
              width: "20px",
              stroke: "black",
              strokeWidth: "1px",
            },
          },
          ".MuiChartsLegend-series:nth-of-type(1)": {
            ".MuiChartsLegend-mark": {
              strokeDasharray: "5 5",
              strokeDashoffset: "2.5px",
            },
          },
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ minWidth: 120 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            {viewState.scale === "monthly" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0,
                  minWidth: "190px",
                }}
              >
                <NavigateBeforeRounded
                  sx={{
                    cursor: isPreviousIncrementPossible ? "pointer" : "auto",
                    color: isPreviousIncrementPossible ? "black" : "lightgray",
                  }}
                  onClick={handlePrevIncrement}
                />
                <Box>{currentPeriodLabel.current}</Box>
                <NavigateNextRounded
                  sx={{
                    cursor: isNextIncrementPossible ? "pointer" : "auto",
                    color: isNextIncrementPossible ? "black" : "lightgray",
                  }}
                  onClick={handleNextIncrement}
                />
              </Box>
            )}
            <FormControl fullWidth>
              <Select
                value={viewState.scale}
                onChange={handleScaleChange}
                sx={{
                  width: 120,
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                {SCALES.map((scale) => (
                  <MenuItem key={scale.value} value={scale.value}>
                    {scale.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
    </MuiLineChartCard>
  );
};
